/* Wrapped in a function so nothing here is a global binding.

   Two sections on one page that both use the slider — the panel slider and a
   collage set to horizontal scroll — render this file twice, and a top-level
   `class PanelSlider` is a lexical binding like `let`: the second execution
   throws "already declared" before it ever reaches the guard at the bottom.
   Inside a function the class is local, so the second run is a harmless no-op. */
(function () {
/* Arrow and counter behaviour for the panel slider.

   The track is a native scroll-snap container, so swiping already works with no
   JS at all — this only adds the buttons and the "3 of 10" readout on top. If
   the script fails to load the section still scrolls; only the arrows go. */

class PanelSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;

    this.panels = Array.from(this.track.querySelectorAll('[data-panel]'));
    this.prev = this.querySelector('[data-action="prev"]');
    this.next = this.querySelector('[data-action="next"]');

    /* Both are optional — a section that shows arrows alone renders neither. */
    this.currentOutput = this.querySelector('[data-current]');
    this.totalOutput = this.querySelector('[data-total]');
    if (this.totalOutput) this.totalOutput.textContent = this.panels.length;

    this.onScroll = this.onScroll.bind(this);
    this.frame = null;
    this.index = 0;

    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.update();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  /* Scroll events fire far faster than the layout can usefully be read, so the
     work is collapsed into one read per frame. */
  onScroll() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  /* One panel's advance is its own width plus the gap. Measured rather than
     computed from settings, so it stays right at any breakpoint. */
  stride() {
    if (this.panels.length < 2) return this.track.clientWidth;
    return this.panels[1].offsetLeft - this.panels[0].offsetLeft;
  }

  /* Stepping from the panel the counter is showing, not from the raw scroll
     position. Mid-swipe the track sits between two panels, and scrollBy from
     there lands between two more — so repeated arrow presses drift further and
     further off a panel edge. Going to an absolute offset re-seats it. */
  step(direction) {
    const target = Math.min(this.panels.length - 1, Math.max(0, this.indexFromScroll() + direction));
    this.track.scrollTo({ left: this.stride() * target, behavior: 'smooth' });
  }

  indexFromScroll() {
    const stride = this.stride();
    if (!stride) return 0;
    return Math.min(this.panels.length - 1, Math.max(0, Math.round(this.track.scrollLeft / stride)));
  }

  update() {
    /* A rounding allowance: scrollLeft lands on fractional pixels at many zoom
       levels and on hi-dpi screens, so an exact comparison would leave the last
       arrow enabled — or disabled a pixel early. */
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    if (this.prev) this.prev.disabled = this.track.scrollLeft <= 1;
    if (this.next) this.next.disabled = this.track.scrollLeft >= maxScroll - 1;

    if (!this.currentOutput) return;

    /* At the very end the last panels share one screen, so the rounded index
       stops short of the final one and the counter would never reach its total.
       Pinning it at the end is what the reader expects. */
    const index = this.track.scrollLeft >= maxScroll - 1 ? this.panels.length - 1 : this.indexFromScroll();
    if (index === this.index) return;
    this.index = index;
    this.currentOutput.textContent = index + 1;
  }
}

if (!customElements.get('panel-slider')) {
  customElements.define('panel-slider', PanelSlider);
}
})();
